import type { UserFlagsT, UserResponseT } from '@mentimeter/http-clients';
import { PlanCategory } from '@mentimeter/http-clients';
import { getNameInitials } from './initials';
import {
  PaymentsProvider,
  type UserRequestT,
  type UserT,
  type UserUpdateT,
} from './types';

export const isPaymentsProvider = (
  value: unknown,
): value is PaymentsProvider => {
  return (
    typeof value === 'string' &&
    Object.values(PaymentsProvider).includes(value as PaymentsProvider)
  );
};

export const serverToState = (data: UserResponseT): UserT => {
  return {
    createdAt: data.created_at,
    id: data.id,
    name: data.name,
    address: data.address ?? '',
    email: data.email,
    externalIdentities: data.external_identities ?? [],
    loginMethod: data.saml
      ? 'sso'
      : data.external_identities?.length
        ? 'social'
        : 'password',
    receiveResultsEmail: data.flags?.receive_results_email ?? false,
    aiFeaturesEnabled: data.flags?.ai_features_enabled ?? false,
    optOutCsat: data.flags?.opt_out_csat ?? false,
    onboarding: String(data.flags?.onboarding),
    hasPassword: Boolean(data.has_password),
    hasSso: data.saml,
    initials: getNameInitials(data.name),
    isFreeUser: data.features.type === 'registered',
    features: {
      ...data.features,
      series_in_workspace_limit:
        data.features.series_in_workspace_limit === -1
          ? Infinity
          : data.features.series_in_workspace_limit,
    },
    language: data.language,
    region: data.region,
    payments_provider: isPaymentsProvider(data.payments_provider)
      ? data.payments_provider
      : PaymentsProvider.UNKNOWN,
    role: data.role,
    ownerOrAdmin: ['admin', 'owner'].includes(data.role ?? ''),
    workspaceId: data.active_workspace_id,
    betaStatus: data.beta_status,
    intercomHash: data.intercom_hash ?? '',
    flags: data.flags,
    collaboration_rights: data.collaboration_rights,
    profile_picture: data.profile_picture,
    service_urls: data.service_urls,
    isVerified: data.is_verified,
    requiresEmailVerification: data.requires_email_verification,
    token: data.token,
    subscriptionPlanCategory:
      data.subscription?.plan.category ?? PlanCategory.FREE,
  };
};

export const stateToServer = (data: UserUpdateT): UserRequestT => {
  let flags: UserFlagsT = {};

  if (data.receiveResultsEmail !== undefined) {
    flags = {
      receive_results_email: data.receiveResultsEmail,
    };
  }
  if (data.aiFeaturesEnabled !== undefined) {
    flags = {
      ai_features_enabled: data.aiFeaturesEnabled,
    };
  }
  if (data.optOutCsat !== undefined) {
    flags = {
      opt_out_csat: data.optOutCsat,
    };
  }

  if (data.flags?.role_changed !== undefined) {
    flags = {
      role_changed: data.flags.role_changed,
    };
  }

  const res = {
    name: data.name,
    address: data.address,
    email: data.email,
    flags,
    password: data.password,
    current_password: data.currentPassword,
    beta_status: data.betaStatus,
    language: data.language,
  } as UserRequestT;
  Object.keys(res).forEach((k) => {
    const key = k as keyof typeof res;
    if (res[key] === undefined) {
      delete res[key];
    }
  });

  return res;
};
